import type { CSSProperties } from "react";
import type { PurposeFamilyId } from "./catalogue";
import {
  normalizeSearch,
  type SearchSettings,
} from "@/components/search/search-block";
import {
  pageDialogOf,
  type PageDialogSettings,
} from "@/components/page-dialog/settings";
import { ARTICLE_FILTERS, ARTICLE_PAGINATIONS } from "@/blog/article-list";

export interface PurposeItem {
  id?: string;
  title?: string;
  text?: string;
  imageUrl?: string;
  imagePosition?: string;
  icon?: string;
  iconId?: string | null;
  linkIconId?: string | null;
  linkIconPosition?: "before" | "after";
  /** the item's button style, as `buttons[].style` */
  linkStyle?: ButtonStyle;
  link?: string;
  linkText?: string;
  group?: string;
  value?: string;
  unit?: string;
  facts?: string;
  price?: string;
  priceNote?: string;
  features?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  a?: string;
  b?: string;
  c?: string;
  author?: string;
  rating?: number;
  /** Kundeudtalelser: `false` draws this testimonial without quote marks; only while the section's own is not `false` */
  showQuoteMarks?: boolean;
  iconImageUrl?: string;
  openInNewTab?: boolean;
  /** Billedgalleri: `videoUrl` plays in the picture's place only with `"video"`; `imageUrl` is its poster */
  mediaKind?: string;
  videoUrl?: string;
  videoType?: string;
}
export interface PurposeAreaStyle {
  padding?: string;
  margin?: string;
  rounded?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: "none" | "thin" | "thick";
  shadow?: "none" | "sm" | "md" | "lg";
  align?: "left" | "center" | "right";
  fontSize?: string;
  fontWeight?: string;
  fontStyle?: "normal" | "italic";
  lineHeight?: string;
  width?: string;
  /** Formular med intro's Billede ved siden af: frosted glass behind the text-and-form column (8, 16 or 24 px). */
  blur?: "none" | "sm" | "md" | "lg";
  /** A rule under the heading or item title: short (80 px) or the text's full width. */
  rule?: "none" | "short" | "full";
  ruleColor?: string;
  textTransform?: "none" | "uppercase";
  letterSpacing?: string;
  /** Buttons only: the button component's own text size and hover colours. */
  size?: string;
  hoverTextColor?: string;
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
}
export const PURPOSE_STYLE_AREAS = [
  "content",
  "items",
  "image",
  "heading",
  "eyebrow",
  "text",
  "itemTitle",
  "itemText",
  "icon",
  "copy",
  "statValue",
  "date",
  "card",
  "map",
  "button",
  "secondaryButton",
  "form",
  "question",
  "answer",
  "frame",
  "quoteMark",
  "itemName",
  "itemRole",
  "rating",
  "caseCard",
  "itemEyebrow",
  "itemCopy",
] as const;
export type PurposeStyleArea = (typeof PURPOSE_STYLE_AREAS)[number];
export const PROPERTY_TAB_FILTERS = [
  "newest",
  "sold",
  "rented",
  "openhouse",
] as const;
export const PROPERTY_TAB_LABELS: Record<
  (typeof PROPERTY_TAB_FILTERS)[number],
  string
> = {
  newest: "Til salg",
  sold: "Solgte",
  rented: "Udlejede",
  openhouse: "Åbent hus",
};
export const PROPERTY_STATUSES = [
  "active",
  "sold",
  "rented",
  "openhouse",
] as const;
export const PROPERTY_SORT_KEYS = [
  "date_desc",
  "date_asc",
  "price_asc",
  "price_desc",
  "postal_code_desc",
  "postal_code_asc",
  "housing_area_desc",
  "housing_area_asc",
] as const;
export const PROPERTY_CASE_TYPES = [
  "all",
  "sale",
  "homeSale",
  "rental",
  "commercial",
  "agricultural",
  "homeAgriculturalSale",
  "homeAgriculturalRental",
] as const;
export interface PurposePropertyTab {
  filter: (typeof PROPERTY_TAB_FILTERS)[number];
  label?: string;
  emptyText?: string;
  /** shown instead of the section's heading while the tab is selected */
  heading?: string;
}
/** Ejendomme's »Se flere« button, a gen-2 button whose standard icon is a plus in a circle before the text. */
export interface ShowMoreButton {
  text: string;
  style: ButtonStyle;
  iconId: string | null;
  iconPosition: "before" | "after";
}
export const BUTTON_STYLES = [
  "primary",
  "secondary",
  "filled",
  "outline",
  "link",
] as const;
export type ButtonStyle = (typeof BUTTON_STYLES)[number];
export interface ImageTextButton {
  text: string;
  link: string;
  iconId?: string | null;
  iconPosition?: "before" | "after";
  style: ButtonStyle;
  dialog?: PageDialogSettings;
}
/** `{ dialog }` when the button opens its page in a dialog (`openIn: "dialog"`), else nothing. */
export function dialogPart(
  button: Record<string, unknown>,
  link: string | undefined,
): { dialog?: PageDialogSettings } {
  const dialog = pageDialogOf(button, link);
  return dialog ? { dialog } : {};
}
export const MAX_BUTTONS = 4;
/** Kontaktperson's narrow card takes two; Arrangement's row is its registration button and three more. */
export const FAMILY_BUTTON_LIMITS: Partial<Record<PurposeFamilyId, number>> = {
  person: 2,
  event: 3,
};
export const BUTTON_LAYOUTS = ["row", "pairs", "stack"] as const;
export type ButtonLayout = (typeof BUTTON_LAYOUTS)[number];
/** `row`, `pairs` or `stack`, or `mobile | desktop`; anything else is undefined. */
export function buttonLayoutValue(raw: unknown): string | undefined {
  const parts = string(raw)
    .split("|")
    .map((part) => part.trim());
  if (
    parts.length > 2 ||
    !parts.every((part) => (BUTTON_LAYOUTS as readonly string[]).includes(part))
  )
    return undefined;
  return parts.join(" | ");
}
export function buttonLayouts(value?: string): [ButtonLayout, ButtonLayout] {
  const [mobile = "row", desktop = mobile] = (value ?? "row")
    .split("|")
    .map((part) => part.trim()) as ButtonLayout[];
  return [mobile, desktop];
}
/** Sammenligning: one compared choice, stored in the list `columns`; its button is drawn under the column. */
export interface ComparedColumn {
  name?: string;
  button?: {
    text?: string;
    link?: string;
    style?: ButtonStyle;
    iconId?: string | null;
    iconPosition?: "before" | "after";
    dialog?: PageDialogSettings;
  };
}
export const SECTION_ICON_POSITIONS = [
  "above",
  "before-heading",
  "after-heading",
  "before-eyebrow",
  "after-eyebrow",
] as const;
export type SectionIconPosition = (typeof SECTION_ICON_POSITIONS)[number];
const IMAGE_WIDTH_FRACTIONS = ["1/4", "1/3", "2/5", "1/2", "3/5", "2/3"];
const LEAD_SHARES = ["1/2", "3/5", "2/3"];
function shareValue(raw: unknown, fractions: string[]): string | undefined {
  const value = string(raw);
  if (fractions.includes(value)) return value;
  const percent = /^([1-9]\d)%$/.exec(value);
  return percent && Number(percent[1]) <= 90 ? value : undefined;
}
/** A fraction from the cards, or an own share of 10–90 % written as `"45%"`. */
export function imageWidthValue(raw: unknown): string | undefined {
  return shareValue(raw, IMAGE_WIDTH_FRACTIONS);
}
/** Ét stort og to små: the big card's share, `1/2`, `3/5`, `2/3` or an own `"NN%"` of 10–90 as `imageWidth` takes it. */
export function leadShareValue(raw: unknown): string | undefined {
  return shareValue(raw, LEAD_SHARES);
}
/** Kontaktperson › Tekst ved siden af kortet: `auto` shows the picture above the text in its own format, uncropped. */
export const PICTURE_RATIOS = [
  "auto",
  "4/3",
  "3/2",
  "16/9",
  "2/1",
  "21/9",
  "1/1",
] as const;
export interface PurposeSettings {
  variant?: number;
  heading?: string;
  headingLevel?: "h1" | "h2" | "h3";
  iconId?: string | null;
  iconPosition?: SectionIconPosition;
  eyebrow?: string;
  text?: string;
  imageUrl?: string;
  imagePosition?: string;
  imageRatio?: string;
  imageFit?: "cover" | "contain";
  items?: PurposeItem[];
  buttonText?: string;
  buttonIconId?: string | null;
  buttonIconPosition?: "before" | "after";
  /** Arrangement: the registration button's style */
  buttonStyle?: ButtonStyle;
  buttonLink?: string;
  tabs?: PurposePropertyTab[];
  openFirst?: boolean;
  allowMultiple?: boolean;
  sourceNote?: string;
  sourceLink?: string;
  updated?: string;
  featuredIndex?: number;
  /** Priser og pakker › Én fremhævet pakke: the label over the featured package (»Anbefalet«; blank hides it) and its border */
  featuredLabel?: string;
  featuredBorderWidth?: "thin" | "thick";
  featuredBorderColor?: string;
  priceNote?: string;
  /** Sammenligning: `columns` when it is a list (Kolonner is a string in the other list types); at most three */
  comparedColumns?: ComparedColumn[];
  /** -1 highlights no column */
  highlightColumn?: number;
  highlightColor?: string;
  firstColumnHeading?: string;
  /** a CSS length or spacing token */
  columnMinWidth?: string;
  /** Logoer og mærker: a logo's largest height, a CSS length or spacing token */
  logoHeight?: string;
  note?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  place?: string;
  afterEvent?: "archive" | "hide";
  /** Arrangement: the label over a past event (»Afholdt« when absent; blank hides it) */
  pastLabel?: string;
  /** Arrangement › Dato i fokus and Kompakt invitation, like `gap`: between the date box and the text; the box's least
   *  width, a CSS length */
  dateGap?: string;
  dateWidth?: string;
  registration?: "none" | "link" | "form";
  formType?: "ContactForm" | "AssessmentForm" | "WishForm";
  formConfig?: Record<string, unknown>;
  resourceGroupId?: string;
  actor?: unknown;
  selectedItems?: unknown[];
  sourceMode?: "auto" | "selected";
  count?: number;
  sort?: "source" | "title";
  showRole?: boolean;
  showPhone?: boolean;
  showEmail?: boolean;
  showAddress?: boolean;
  showHours?: boolean;
  /** Kontakt og åbningstider: the store's name, CVR line and social profiles (each shown while absent), the icons'
   *  look and size in px (16, 20 or 24), a rich text with store fields under the details, Kort og oplysninger's and
   *  Kontaktkort's map or picture (absent: the layout's own), the intro beside the details or the card, and Kort og
   *  oplysninger's details in Kontaktkort's card */
  showName?: boolean;
  showCvr?: boolean;
  showSocial?: boolean;
  socialStyle?: "outline" | "solid";
  socialIconSize?: number;
  extraText?: string;
  media?: "map" | "image" | "none";
  introPlacement?: "beside";
  detailsCard?: boolean;
  /** Medarbejdere and Kontaktperson: »Gem kontakt« as an icon by the name (the standard), a line under the contact
   *  lines with `vcardLabel`, or none; `imageBleed` puts the portrait at the card's edge */
  vcard?: "icon" | "link" | "none";
  vcardLabel?: string;
  imageBleed?: boolean;
  /** Kontakt og åbningstider: the contact block's field names, the hours' heading (»Åbningstider« when absent), the
   *  map's height (a CSS length) and, like `gap`, the space between the contact block and the hours where they stack */
  showLabels?: boolean;
  hoursTitle?: string;
  mapHeight?: string;
  hoursGap?: string;
  showDate?: boolean;
  showAuthor?: boolean;
  showExcerpt?: boolean;
  blogId?: string;
  articleTag?: string;
  detailsPageSlug?: string;
  /** Artikler on an artikelside with a path: which articles (»Følg siden« is `page`), »Emne« and »Forfatter« for the
   *  `topic` and `author` filters, page links or »Vis flere«, topic tabs and a search box. Absent: `all`, `none`, off. */
  articleFilter?: (typeof ARTICLE_FILTERS)[number];
  topicId?: string;
  authorId?: string;
  pagination?: (typeof ARTICLE_PAGINATIONS)[number];
  topicTabs?: boolean;
  showSearch?: boolean;
  propertyStatus?: (typeof PROPERTY_STATUSES)[number];
  sortKey?: (typeof PROPERTY_SORT_KEYS)[number];
  caseType?: (typeof PROPERTY_CASE_TYPES)[number];
  includeJustSold?: boolean;
  query?: string;
  skip?: number;
  /** absent: the website's own `soldExpirationInDays` applies */
  soldExpirationInDays?: number;
  /** Ejendomme: »Se flere« adds `count` cases per click until Core's total (automatic source, layout 0 or 1), the gallery
   *  card in layout 0; `emptyText` is the empty list's text without tabs */
  showMore?: boolean;
  showMoreButton?: ShowMoreButton;
  card?: "editorial" | "gallery";
  estateType?: string;
  showCaptions?: boolean;
  lightbox?: boolean;
  /** Medie og tekst's search bar (`search.placement`) and the page it sends the search word to */
  search?: SearchSettings;
  targetPage?: string;
  /** Genveje, Medarbejdere and Ejendomme: Kortene glider ind */
  flyIn?: boolean;
  /** Medie og tekst and Fordele's Liste med billede: `videoUrl` plays only with `"video"`. */
  mediaKind?: "image" | "video";
  videoUrl?: string;
  videoType?: "realequity" | "vimeo" | "youtube";
  contentPosition?: "top" | "center" | "bottom";
  /** the share of a split's first part; in Formular med intro the part without the form: the text (Intro ved siden
   *  af), the picture and the text (Billede og formular) or the media (Billede ved siden af) */
  imageWidth?: string;
  /** `alternate` only in Kundeudtalelser's Citat i fokus: every second photo on the right. In Formular med intro the
   *  text's side on the computer: absent is `left`, in Billede ved siden af `right`, so its media stands on the left */
  placement?: "left" | "right" | "top" | "behind" | "none" | "alternate";
  textPanel?: "beside" | "overlap";
  /** Medie og tekst without a picture: the buttons below the text or beside it on the same row */
  actionsPlacement?: "below" | "beside";
  /** what comes first on the phone; `questions` only in Spørgsmål og svar's Svar ved siden af, `form` only in Formular
   *  med intro's Intro ved siden af and Billede og formular, where absent is `text`; in Billede ved siden af `text` puts
   *  the text and form first and anything else the media. The DOM follows it, so it is also the reading order */
  mobileOrder?: "image" | "text" | "questions" | "form" | "embed";
  /** Formular med intro's Billede og formular and Billede ved siden af: below 768 px the media is not drawn */
  hideMediaOnMobile?: boolean;
  /** Formular med intro › Billede ved siden af: `person` puts the employee in `actor` on Kontaktperson's card where the
   *  media stands (absent: the media) */
  sideContent?: "person";
  /** Butikskort: an own logo from Medier in place of the store's Boligsiden logo, `showLogo: false` none; a line under
   *  the name such as »v/ Anne Holm«; the store's legal name under it (only with `true`) */
  logoUrl?: string;
  showLogo?: boolean;
  byLine?: string;
  showLegalName?: boolean;
  tint?: "none" | "dark" | "light";
  buttons?: ImageTextButton[];
  /** Knappernes opstilling: `row`, `pairs` or `stack`, or `mobile | desktop` (absent: side by side) */
  buttonLayout?: string;
  /** the space between the two sides of a split (picture and text, text and form, map and details) or between
   *  Tekstafsnit's text columns, a spacing token or a CSS length; in Formular med intro also `mobile | desktop`, like
   *  its `introGap`, and 0 by default beside a panel in Billede ved siden af */
  gap?: string;
  /** Medie og tekst, like `gap`: between the parts of the text block, above the buttons, between the buttons,
   *  and how far the overlap panel reaches over the picture */
  copyGap?: string;
  /** every family: between the title/intro block and what follows it, like `gap` */
  introGap?: string;
  actionsGap?: string;
  buttonGap?: string;
  overlap?: string;
  /** Tidslinje: between the step number and its text, like `gap` */
  numberGap?: string;
  /** Spørgsmål og svar: between the question and its sign, like `gap` */
  questionGap?: string;
  leadShare?: string;
  /** Billedgalleri, like `gap`: above a caption; above a carousel's controls and between them (read by the shared
   *  carousel; Sagsliste spaces its toolbar with controlsGap through --pf-controls-gap) */
  captionGap?: string;
  controlsGap?: string;
  arrowGap?: string;
  /** Kontaktperson, like `gap`: above the badges and between them; Enkelt profilkort's width, a CSS length */
  badgesGap?: string;
  badgeGap?: string;
  cardWidth?: string;
  /** Kontaktperson › Tekst ved siden af kortet: the text column's share as `leadShare` takes it, the space between the
   *  text and the card (also `mobile | desktop`) and under the picture, like `gap`; the picture's format and focus point */
  textWidth?: string;
  cardGap?: string;
  pictureGap?: string;
  pictureRatio?: (typeof PICTURE_RATIOS)[number];
  picturePosition?: string;
  /** Kundeudtalelser, like `gap`: above the note; Enkel citatkarrusel's text width, a CSS length */
  noteGap?: string;
  quoteWidth?: string;
  /** beside a picture in an item (Kontaktperson, Citat i fokus): where its text stands vertically */
  itemPosition?: "top" | "center" | "bottom";
  /** Nøgletal, Områdeguide's cards and Priser og pakker, like `gap`: between the parts of each number or card */
  itemGap?: string;
  /** Nøgletal, like `gap`: above the source */
  sourceGap?: string;
  /** with `collapseText`: the folded text's height, a CSS length */
  collapseHeight?: string;
  /** Medie og tekst and Liste med billede: how a video in `videoUrl` plays */
  video?: {
    autoplay: boolean;
    muted: boolean;
    loop: boolean;
    controls: boolean;
  };
  imageHeight?: string;
  cardLink?: boolean;
  collapseText?: boolean;
  /** Kundeudtalelser: `false` draws no quote marks in any layout */
  showQuoteMarks?: boolean;
  showBoligsidenBadge?: boolean;
  boligsidenBadgeId?: string;
  showBuyerAdviceBadge?: boolean;
  transcriptLink?: string;
  quote?: string;
  quoteAuthor?: string;
  rowGap?: string;
  columnGap?: string;
  columns?: string;
  /** Indlejret side: the page's address (embed-hosts.ts decides whether it is drawn), the frame's title, its height as
   *  `phone | desktop`, whether a provider's height messages set it, and the link that opens the page in a new window */
  embedUrl?: string;
  embedTitle?: string;
  embedHeight?: string;
  autoHeight?: boolean;
  showOpenLink?: boolean;
  openLinkText?: string;
  /** Samarbejdsaftaler: the blocks (absent: the two default blocks), what an empty value shows or whether its line is
   *  left out, and Tabel's label column */
  blocks?: DisclosureBlockSettings[];
  hideEmpty?: boolean;
  emptyText?: string;
  labelWidth?: string;
  styles?: Partial<Record<PurposeStyleArea, PurposeAreaStyle>>;
}
/** One afsnit of Samarbejdsaftaler as stored; disclosure.ts draws it from the store's data. */
export interface DisclosureBlockSettings {
  id?: string;
  /** absent: an own text block; an unknown kind is skipped */
  kind?: string;
  title?: string;
  text?: string;
  note?: string;
  showName?: boolean;
  showAddress?: boolean;
  showPhone?: boolean;
  showEmail?: boolean;
  showCvr?: boolean;
  showLogos?: boolean;
  showRole?: boolean;
}
const BLOCK_TEXT_KEYS = ["id", "kind", "title", "text", "note"] as const;
const BLOCK_FLAGS = [
  "showName",
  "showAddress",
  "showPhone",
  "showEmail",
  "showCvr",
  "showLogos",
  "showRole",
] as const;
/** At most 20 blocks; absent stays absent, so the default blocks apply. */
export function normalizeBlocks(
  raw: unknown,
): DisclosureBlockSettings[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .slice(0, 20)
    .filter((item) => item && typeof item === "object" && !Array.isArray(item))
    .map((item) => {
      const block: DisclosureBlockSettings = {};
      for (const key of BLOCK_TEXT_KEYS)
        if (typeof item[key] === "string") block[key] = item[key];
      for (const key of BLOCK_FLAGS)
        if (typeof item[key] === "boolean") block[key] = item[key];
      return block;
    });
}
export type PurposeStyle = CSSProperties &
  Record<`--${string}`, string | number | undefined>;

export function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
export function string(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
/** `value` when it is one of `values`, otherwise the first, which is the standard */
function oneOf<T extends string>(values: readonly T[], value: unknown): T {
  return (values as readonly unknown[]).includes(value)
    ? (value as T)
    : values[0];
}
/** How a video plays; each flag absent keeps Medie og tekst's default (autoplay, muted, loop, no controls). */
export function normalizeVideoPlayback(
  raw: unknown,
): NonNullable<PurposeSettings["video"]> {
  const video = record(raw);
  const flag = (key: string, fallback: boolean) =>
    typeof video[key] === "boolean" ? (video[key] as boolean) : fallback;
  return {
    autoplay: flag("autoplay", true),
    muted: flag("muted", true),
    loop: flag("loop", true),
    controls: flag("controls", false),
  };
}
export function referenceId(value: unknown): string {
  if (typeof value === "string") return value;
  const ref = record(value);
  return (
    string(ref.id) ||
    string(ref.actorId) ||
    string(ref.caseId) ||
    string(ref.resourceGroupId)
  );
}
/** Only new opt-in family payloads use these defaults. Old section types never enter here. `variants` is the family's layout count. */
export function normalizePurposeSettings(
  raw: unknown,
  variants = 3,
  familyId?: PurposeFamilyId,
): PurposeSettings {
  const value = record(raw);
  const settings: PurposeSettings = {};
  const textKeys = [
    "heading",
    "eyebrow",
    "text",
    "imageUrl",
    "imagePosition",
    "imageRatio",
    "buttonText",
    "buttonLink",
    "sourceNote",
    "sourceLink",
    "updated",
    "featuredLabel",
    "featuredBorderColor",
    "priceNote",
    "highlightColor",
    "firstColumnHeading",
    "columnMinWidth",
    "logoHeight",
    "note",
    "date",
    "startTime",
    "endTime",
    "timeZone",
    "place",
    "pastLabel",
    "dateGap",
    "dateWidth",
    "hoursTitle",
    "mapHeight",
    "hoursGap",
    "vcardLabel",
    "extraText",
    "resourceGroupId",
    "blogId",
    "articleTag",
    "detailsPageSlug",
    "estateType",
    "videoUrl",
    "gap",
    "copyGap",
    "introGap",
    "actionsGap",
    "buttonGap",
    "overlap",
    "numberGap",
    "questionGap",
    "collapseHeight",
    "itemGap",
    "sourceGap",
    "captionGap",
    "controlsGap",
    "arrowGap",
    "badgesGap",
    "badgeGap",
    "cardWidth",
    "cardGap",
    "pictureGap",
    "noteGap",
    "quoteWidth",
    "transcriptLink",
    "quote",
    "quoteAuthor",
    "rowGap",
    "columnGap",
    "columns",
    "imageHeight",
    "boligsidenBadgeId",
    "targetPage",
    "embedUrl",
    "embedTitle",
    "embedHeight",
    "openLinkText",
    "emptyText",
    "labelWidth",
    "logoUrl",
    "byLine",
  ] as const;
  for (const key of textKeys)
    if (typeof value[key] === "string") settings[key] = value[key];
  const flags = [
    "openFirst",
    "allowMultiple",
    "showRole",
    "showPhone",
    "showEmail",
    "showAddress",
    "showHours",
    "showName",
    "showCvr",
    "showSocial",
    "detailsCard",
    "imageBleed",
    "showLabels",
    "showDate",
    "showAuthor",
    "showExcerpt",
    "showCaptions",
    "lightbox",
    "cardLink",
    "collapseText",
    "showMore",
    "showQuoteMarks",
    "showBoligsidenBadge",
    "showBuyerAdviceBadge",
    "autoHeight",
    "showOpenLink",
    "hideEmpty",
    "hideMediaOnMobile",
    "showLogo",
    "showLegalName",
    "flyIn",
  ] as const;
  for (const key of flags)
    if (typeof value[key] === "boolean") settings[key] = value[key];
  settings.variant =
    Number.isInteger(value.variant) &&
    Number(value.variant) >= 0 &&
    Number(value.variant) < variants
      ? Number(value.variant)
      : 0;
  // »Alle« shows every person; 100 bounds what a store's list can return. Medarbejdere shows everyone while absent.
  settings.count =
    value.count === "all"
      ? 100
      : Number.isInteger(value.count)
        ? Math.max(1, Math.min(24, Number(value.count)))
        : familyId === "team"
          ? 100
          : 3;
  settings.featuredIndex = Number.isInteger(value.featuredIndex)
    ? Math.max(0, Number(value.featuredIndex))
    : 0;
  settings.highlightColumn = Number.isInteger(value.highlightColumn)
    ? Number(value.highlightColumn)
    : 1;
  settings.formType = ["ContactForm", "AssessmentForm", "WishForm"].includes(
    string(value.formType),
  )
    ? (value.formType as PurposeSettings["formType"])
    : "ContactForm";
  settings.sourceMode = value.sourceMode === "selected" ? "selected" : "auto";
  settings.sort = value.sort === "title" ? "title" : "source";
  settings.vcard =
    value.vcard === "link" || value.vcard === "none" ? value.vcard : "icon";
  if (value.socialStyle === "outline" || value.socialStyle === "solid")
    settings.socialStyle = value.socialStyle;
  settings.socialIconSize =
    typeof value.socialIconSize === "number" &&
    [16, 20, 24].includes(value.socialIconSize)
      ? value.socialIconSize
      : 20;
  if (
    value.media === "map" ||
    value.media === "image" ||
    value.media === "none"
  )
    settings.media = value.media;
  if (value.introPlacement === "beside") settings.introPlacement = "beside";
  if (value.sideContent === "person") settings.sideContent = "person";
  settings.registration =
    value.registration === "form" || value.registration === "none"
      ? value.registration
      : "link";
  settings.afterEvent = value.afterEvent === "hide" ? "hide" : "archive";
  settings.propertyStatus = oneOf(PROPERTY_STATUSES, value.propertyStatus);
  settings.sortKey = oneOf(PROPERTY_SORT_KEYS, value.sortKey);
  settings.caseType = oneOf(PROPERTY_CASE_TYPES, value.caseType);
  settings.includeJustSold = value.includeJustSold === true;
  if (value.card === "gallery" || value.card === "editorial")
    settings.card = value.card;
  if (value.showMoreButton !== undefined || value.showMore === true) {
    const more = record(value.showMoreButton);
    settings.showMoreButton = {
      text:
        typeof more.text === "string" && more.text.trim()
          ? more.text
          : "Se flere",
      style: (BUTTON_STYLES as readonly unknown[]).includes(more.style)
        ? (more.style as ButtonStyle)
        : "primary",
      iconId:
        more.iconId === null || more.iconId === ""
          ? null
          : typeof more.iconId === "string"
            ? more.iconId
            : "circle-plus",
      iconPosition: more.iconPosition === "after" ? "after" : "before",
    };
  }
  if (typeof value.query === "string" && value.query.trim())
    settings.query = value.query.trim();
  settings.skip =
    Number.isInteger(value.skip) && Number(value.skip) > 0
      ? Number(value.skip)
      : 0;
  if (
    Number.isInteger(value.soldExpirationInDays) &&
    Number(value.soldExpirationInDays) > 0
  )
    settings.soldExpirationInDays = Number(value.soldExpirationInDays);
  settings.videoType =
    value.videoType === "vimeo" || value.videoType === "youtube"
      ? value.videoType
      : "realequity";
  if (["h1", "h2", "h3"].includes(string(value.headingLevel)))
    settings.headingLevel =
      value.headingLevel as PurposeSettings["headingLevel"];
  if (["top", "center", "bottom"].includes(string(value.contentPosition)))
    settings.contentPosition =
      value.contentPosition as PurposeSettings["contentPosition"];
  if (["top", "center", "bottom"].includes(string(value.itemPosition)))
    settings.itemPosition =
      value.itemPosition as PurposeSettings["itemPosition"];
  const imageWidth = imageWidthValue(value.imageWidth);
  if (imageWidth) settings.imageWidth = imageWidth;
  const leadShare = leadShareValue(value.leadShare);
  if (leadShare) settings.leadShare = leadShare;
  const textWidth = leadShareValue(value.textWidth);
  if (textWidth) settings.textWidth = textWidth;
  settings.pictureRatio = oneOf(PICTURE_RATIOS, value.pictureRatio);
  if (value.mediaKind === "image" || value.mediaKind === "video")
    settings.mediaKind = value.mediaKind;
  if (
    (SECTION_ICON_POSITIONS as readonly string[]).includes(
      string(value.iconPosition),
    )
  )
    settings.iconPosition = value.iconPosition as SectionIconPosition;
  if (
    ["left", "right", "top", "behind", "none", "alternate"].includes(
      string(value.placement),
    )
  )
    settings.placement = value.placement as PurposeSettings["placement"];
  if (
    value.featuredBorderWidth === "thin" ||
    value.featuredBorderWidth === "thick"
  )
    settings.featuredBorderWidth = value.featuredBorderWidth;
  if (value.textPanel === "beside" || value.textPanel === "overlap")
    settings.textPanel = value.textPanel;
  if (
    ["image", "text", "questions", "form", "embed"].includes(
      string(value.mobileOrder),
    )
  )
    settings.mobileOrder = value.mobileOrder as PurposeSettings["mobileOrder"];
  if (value.actionsPlacement === "below" || value.actionsPlacement === "beside")
    settings.actionsPlacement = value.actionsPlacement;
  if (["none", "dark", "light"].includes(string(value.tint)))
    settings.tint = value.tint as PurposeSettings["tint"];
  if (value.video !== undefined)
    settings.video = normalizeVideoPlayback(value.video);
  const search = normalizeSearch(value.search);
  if (search) settings.search = search;
  if (Array.isArray(value.buttons))
    settings.buttons = purposeButtons(value.buttons);
  const buttonLayout = buttonLayoutValue(value.buttonLayout);
  if (buttonLayout) settings.buttonLayout = buttonLayout;
  for (const key of ["iconId", "buttonIconId"] as const) {
    if (value[key] === null || typeof value[key] === "string")
      settings[key] = value[key] as string | null;
  }
  if (
    value.buttonIconPosition === "before" ||
    value.buttonIconPosition === "after"
  )
    settings.buttonIconPosition = value.buttonIconPosition;
  if ((BUTTON_STYLES as readonly unknown[]).includes(value.buttonStyle))
    settings.buttonStyle = value.buttonStyle as ButtonStyle;
  if (Array.isArray(value.columns))
    settings.comparedColumns = value.columns
      .slice(0, 3)
      .map(record)
      .map(comparedColumn);
  settings.imageFit = value.imageFit === "contain" ? "contain" : "cover";
  settings.formConfig = record(value.formConfig);
  const focus = record(value.imageFocus);
  if (
    ["left", "center", "right"].includes(string(focus.x)) &&
    ["top", "center", "bottom"].includes(string(focus.y))
  )
    settings.imagePosition = `${focus.x} ${focus.y}`;
  const pictureFocus = record(value.pictureFocus);
  if (
    ["left", "center", "right"].includes(string(pictureFocus.x)) &&
    ["top", "center", "bottom"].includes(string(pictureFocus.y))
  )
    settings.picturePosition = `${pictureFocus.x} ${pictureFocus.y}`;
  settings.actor = value.actor;
  settings.selectedItems = Array.isArray(value.selectedItems)
    ? value.selectedItems.slice(0, 100)
    : [];
  settings.items = Array.isArray(value.items)
    ? value.items
        .slice(0, 100)
        .filter(
          (item) => item && typeof item === "object" && !Array.isArray(item),
        )
        .map((item) => {
          const result: PurposeItem = {};
          for (const [key, content] of Object.entries(record(item))) {
            if (["rating", "fileSize"].includes(key)) {
              if (
                typeof content === "number" &&
                Number.isFinite(content) &&
                content >= 0
              )
                (result as Record<string, unknown>)[key] =
                  key === "rating" ? Math.min(5, Math.floor(content)) : content;
            } else if (
              (key === "iconId" || key === "linkIconId") &&
              content === null
            )
              (result as Record<string, unknown>)[key] = null;
            else if (key === "linkStyle") {
              if ((BUTTON_STYLES as readonly unknown[]).includes(content))
                result.linkStyle = content as ButtonStyle;
            } else if (key === "openInNewTab" || key === "showQuoteMarks") {
              if (typeof content === "boolean") result[key] = content;
            } else if (typeof content === "string" && key !== "imageAlt")
              (result as Record<string, unknown>)[key] = content;
          }
          const focus = record(record(item).imageFocus);
          if (
            ["left", "center", "right"].includes(string(focus.x)) &&
            ["top", "center", "bottom"].includes(string(focus.y))
          )
            result.imagePosition = `${focus.x} ${focus.y}`;
          return result;
        })
    : [];
  settings.tabs = (Array.isArray(value.tabs) ? value.tabs : [])
    .map(record)
    .filter((tab) =>
      (PROPERTY_TAB_FILTERS as readonly string[]).includes(string(tab.filter)),
    )
    .slice(0, PROPERTY_TAB_FILTERS.length)
    .map((tab) => ({
      filter: tab.filter as PurposePropertyTab["filter"],
      ...(typeof tab.label === "string" ? { label: tab.label } : {}),
      ...(typeof tab.emptyText === "string"
        ? { emptyText: tab.emptyText }
        : {}),
      ...(typeof tab.heading === "string" && tab.heading.trim()
        ? { heading: tab.heading }
        : {}),
    }));
  const blocks = normalizeBlocks(value.blocks);
  if (blocks) settings.blocks = blocks;
  settings.styles = record(value.styles) as PurposeSettings["styles"];
  // Kun formular draws no text field; its Tekstfarve, hidden in the CMS there, must not colour the form.
  if (
    familyId === "form" &&
    settings.variant === 4 &&
    settings.styles?.content
  ) {
    const content = { ...settings.styles.content };
    delete content.textColor;
    settings.styles = { ...settings.styles, content };
  }
  if (familyId === "articles") {
    settings.articleFilter = oneOf(ARTICLE_FILTERS, value.articleFilter);
    settings.pagination = oneOf(ARTICLE_PAGINATIONS, value.pagination);
    for (const key of ["topicId", "authorId"] as const)
      if (typeof value[key] === "string" && value[key])
        settings[key] = value[key];
    for (const key of ["topicTabs", "showSearch"] as const)
      if (typeof value[key] === "boolean") settings[key] = value[key];
  }
  return settings;
}
/** At most four buttons with a text and a safe link; a button without a style is primary first and secondary after. */
export function purposeButtons(raw: unknown[]): ImageTextButton[] {
  return raw
    .map(record)
    .filter((item) => typeof item.text === "string" && safeUrl(item.link))
    .slice(0, MAX_BUTTONS)
    .map((item, index) => ({
      text: string(item.text),
      link: safeUrl(item.link)!,
      ...dialogPart(item, safeUrl(item.link)),
      ...(typeof item.iconId === "string" || item.iconId === null
        ? { iconId: item.iconId }
        : {}),
      ...(item.iconPosition === "before" || item.iconPosition === "after"
        ? { iconPosition: item.iconPosition }
        : {}),
      style: (BUTTON_STYLES as readonly unknown[]).includes(item.style)
        ? (item.style as ImageTextButton["style"])
        : item.style === undefined && index > 0
          ? "secondary"
          : "primary",
    }));
}
function comparedColumn(raw: Record<string, unknown>): ComparedColumn {
  const button = record(raw.button);
  const kept: NonNullable<ComparedColumn["button"]> = {
    ...(typeof button.text === "string" ? { text: button.text } : {}),
    ...(typeof button.link === "string" ? { link: button.link } : {}),
    ...((BUTTON_STYLES as readonly unknown[]).includes(button.style)
      ? { style: button.style as ButtonStyle }
      : {}),
    ...(typeof button.iconId === "string" || button.iconId === null
      ? { iconId: button.iconId }
      : {}),
    ...(button.iconPosition === "before" || button.iconPosition === "after"
      ? { iconPosition: button.iconPosition }
      : {}),
    ...dialogPart(button, safeUrl(button.link)),
  };
  return {
    ...(typeof raw.name === "string" ? { name: raw.name } : {}),
    ...(raw.button !== undefined ? { button: kept } : {}),
  };
}
export const LIST_FAMILIES = new Set<PurposeFamilyId>([
  "benefits",
  "steps",
  "faq",
  "shortcuts",
  "stats",
  "areas",
  "packages",
  "downloads",
  "logos",
  "compare",
  "gallery",
  "testimonials",
  "team",
  "articles",
  "properties",
  "quote-carousel",
]);
export function safeUrl(raw: unknown): string | undefined {
  const value = string(raw).trim();
  // eslint-disable-next-line no-control-regex -- URL control characters must be rejected.
  if (!value || /[\u0000-\u001f\u007f\\]/.test(value) || value.startsWith("//"))
    return undefined;
  return /^(?:https?:\/\/|mailto:|tel:|\/|#)/i.test(value) ? value : undefined;
}

export function personInitials(name?: string): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const last = words.length > 1 ? words[words.length - 1] : "";
  return `${words[0].charAt(0)}${last.charAt(0)}`.toUpperCase();
}
